import { useEffect } from 'react';

import { setSessionStorageItem } from '@revenuemarket/browser';
import { Button, GrayColors, typographyStyle } from '@revenuemarket/cds';
import { validateCallbackUrl } from '@revenuemarket/utils';

import { OAuthProviderType } from '~apis/auth/maybeSignInWithOAuth';
import { OAUTH_AUTH_DOMAIN } from '~constants/env';

import { generateRandomState, getOauthRedirectUrl } from './utils';

const GoogleIcon = ({ width = 13, height = 13 }: { width?: number; height?: number }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 48 48"
    style={{ display: 'block' }}
    aria-hidden="true"
    focusable="false"
  >
    <g>
      <path
        fill="#4285F4"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#34A853"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#EA4335"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </g>
  </svg>
);

const GoogleOauthButton = ({
  title,
  callbackUrl,
  onGoogleOauthClick,
}: {
  title: string;
  callbackUrl?: string;
  onGoogleOauthClick?: () => void;
}) => {
  const redirectUri = getOauthRedirectUrl(OAuthProviderType.GOOGLE);
  const randomState = generateRandomState();

  const GOOGLE_AUTH_URL =
    `accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=18687547376-be3a1mqhlnvfo1594sr18f465i78mjjf.apps.googleusercontent.com` +
    `&redirect_uri=${redirectUri}` +
    `&response_type=code` +
    `&scope=openid%20profile%20email` +
    `&prompt=select_account` +
    `&state=${encodeURIComponent(randomState)}`;

  // callbackUrl 검증: 상대경로만 허용
  if (callbackUrl && validateCallbackUrl(callbackUrl)) {
    setSessionStorageItem(`oauth_login_callback`, callbackUrl);
  }

  const HTTPS_GOOGLE_AUTH_URL = `https://${GOOGLE_AUTH_URL}`;

  const handleClickGoogleOauthInKakaoTalkBizWebView = () => {
    if (typeof window === 'undefined') {
      return;
    }
    window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(
      `kakaotalk://web/openExternal?url=${encodeURIComponent(window.location.href)}`,
    )}`;
  };

  const isKakaoTalkBizWebView = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const ua = navigator.userAgent;
    return ua.includes('BizWebView') && ua.includes('KAKAOTALK');
  };

  const handleClick = () => {
    if (typeof window === 'undefined') {
      return;
    }

    onGoogleOauthClick?.();

    // finance.cloud 도메인에서는 OAUTH_AUTH_DOMAIN으로 먼저 이동
    if (window.location.hostname.endsWith('.finance.cloud')) {
      const currentOrigin = window.location.origin;
      const encodedOrigin = encodeURIComponent(currentOrigin);
      window.location.href = `${OAUTH_AUTH_DOMAIN}/oauth/init?provider=google&origin=${encodedOrigin}&state=${randomState}`;
      return;
    }

    // 기존 로직: clobe.ai 도메인은 직접 OAuth 제공자로 이동
    const ua = navigator.userAgent;
    const isAndroidWebView = ua.includes('; wv');

    if (isAndroidWebView) {
      window.location.href = `intent://${GOOGLE_AUTH_URL}#Intent;scheme=https;package=com.android.chrome;end;`;
    } else if (isKakaoTalkBizWebView()) {
      handleClickGoogleOauthInKakaoTalkBizWebView();
    } else {
      window.location.href = HTTPS_GOOGLE_AUTH_URL;
    }
  };

  useEffect(() => {
    if (isKakaoTalkBizWebView()) {
      handleClickGoogleOauthInKakaoTalkBizWebView();
    }
  }, []);

  return (
    <Button
      buttonType="default"
      buttonSize="xlarge"
      border
      onClick={handleClick}
      icon={<GoogleIcon width={18} height={18} />}
      style={{
        ...typographyStyle['16_500'],
        color: GrayColors.gray700,
      }}
    >
      {title}
    </Button>
  );
};

export default GoogleOauthButton;
